// Components
import { IconUser } from '@u21/tabler-icons';
import {
  DataProps,
  SidebarExternalLinkList,
} from 'app/modules/sidebar/components/SidebarExternalLinkList';
import {
  U21Subsection,
  U21TitleCountLabel,
  U21Typography,
  U21Loading,
} from 'app/shared/u21-ui/components';

// Models
import { ShortEntityResponse } from 'app/modules/entities/responses';

// Helpers
import { useMemo } from 'react';
import { formatDatetime } from 'app/shared/utils/date';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { useSelector } from 'react-redux';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import { ColdStorageCGDOMessage } from 'app/shared/components/DataLoader/ColdStorageCGDOMessage';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';

interface Props {
  count: number | null;
  entities: ShortEntityResponse[];
  loading: boolean;
  coldStorageLoadingStatus?: ColdStorageLoadingStatus;
}

export const SidebarAssociatedEntities = ({
  count,
  entities,
  loading,
  coldStorageLoadingStatus,
}: Props) => {
  const countLoading =
    loading ||
    coldStorageLoadingStatus === ColdStorageLoadingStatus.LOADING_TIMEOUT;
  const hasReadEntitiesPermission = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const list = useMemo(
    () =>
      entities.map<DataProps>((i) => ({
        label: formatDatetime(i.created_at),
        value: i.external_id,
        id: i.id,
        route: hasReadEntitiesPermission
          ? ROUTES_MAP.entitiesId.path.replace(':id', String(i.id))
          : undefined,
      })),
    [entities, hasReadEntitiesPermission],
  );
  return (
    <U21Subsection
      aria-label="Flagged Entities"
      collapsible
      collapsed
      shadedHeader
      title={
        <U21TitleCountLabel loading={countLoading} count={count} label="">
          Flagged Entities
        </U21TitleCountLabel>
      }
      titleIcon={<IconUser />}
    >
      {coldStorageLoadingStatus && (
        <ColdStorageCGDOMessage
          coldStorageStatus={coldStorageLoadingStatus}
          cgdoType="entity"
        />
      )}
      {loading ? (
        <U21Loading variant="dots" loading={loading} />
      ) : (
        <>
          {entities.length ? (
            <SidebarExternalLinkList list={list} />
          ) : (
            <U21Typography variant="caption" color="text.secondary">
              No flagged entities
            </U21Typography>
          )}
        </>
      )}
    </U21Subsection>
  );
};
